import React, { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { ChartLineUp, Globe, Play, Users, MapPin, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-4">
      <p className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-2 inline-flex items-center gap-2">
        <Icon size={12} weight="bold" /> {label}
      </p>
      <p className="text-3xl font-black tracking-tighter text-white font-mono tabular-nums">{value.toLocaleString()}</p>
    </div>
  );
}

function WorldMap({ pins }) {
  const [light, setLight] = React.useState(false);
  const fill = light ? "#e5e7eb" : "#1c1612";
  const stroke = light ? "#9ca3af" : "#2d231d";
  const hoverFill = light ? "#d1d5db" : "#2d231d";
  const bg = light ? "bg-zinc-100" : "bg-ink-800/30";
  return (
    <div className={`rounded-lg border border-ink-600 ${bg} p-4 overflow-hidden`} data-testid="world-map">
      <div className="flex items-center justify-between mb-2">
        <p className={`text-[10px] font-bold tracking-[0.2em] uppercase ${light ? "text-zinc-700" : "text-zinc-400"}`}>Geo heat map</p>
        <button
          onClick={() => setLight((v) => !v)}
          className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/60 transition-colors"
          data-testid="map-theme-toggle"
        >
          {light ? "🌙 Night mode" : "☀️ Light mode"}
        </button>
      </div>
      <ComposableMap projectionConfig={{ scale: 140 }} style={{ width: "100%", height: "auto" }}>
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill={fill}
                stroke={stroke}
                strokeWidth={0.4}
                style={{ default: { outline: "none" }, hover: { fill: hoverFill, outline: "none" }, pressed: { outline: "none" } }}
              />
            ))
          }
        </Geographies>
        {pins.map((p, i) => (
          <Marker key={`pin-${i}`} coordinates={[p.lon, p.lat]}>
            <circle r={3} fill="#ff8a3c" fillOpacity={0.7} stroke={light ? "#111" : "#fff"} strokeWidth={0.4} />
          </Marker>
        ))}
      </ComposableMap>
      <p className={`mt-2 text-xs font-mono ${light ? "text-zinc-600" : "text-zinc-500"}`}>
        {pins.length} geo-tagged data points
      </p>
    </div>
  );
}

function SlotDrillDown({ rank, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/admin/analytics/slot/${rank}`).then(({ data }) => { setData(data); setLoading(false); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail) || e.message); setLoading(false); });
  }, [rank]);
  return (
    <div className="fixed inset-0 z-[80] bg-ink-900/90 backdrop-blur-xl flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid={`slot-drilldown-${rank}`}>
      <div className="bg-ink-800 border border-neon-cyan/30 rounded-lg max-w-5xl w-full p-6 lg:p-8 my-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] font-bold tracking-[0.3em] text-neon-cyan uppercase">Slot drill-down</p>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tighter">#{String(rank).padStart(2, "0")} — Geographic Reach</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/60 flex items-center justify-center" data-testid="drilldown-close">
            <X size={16} />
          </button>
        </div>
        {loading || !data ? (
          <p className="text-zinc-500 text-sm">Loading...</p>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Plays" value={data.play_count} icon={Play} />
              <StatCard label="Votes" value={data.vote_count} icon={Users} />
              <StatCard label="Heat points" value={data.display_count} icon={ChartLineUp} />
              <StatCard label="Avg rating" value={data.avg_rating} icon={Globe} />
            </div>
            {data.pins?.length > 0 && <WorldMap pins={data.pins} />}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-lg border border-ink-600 bg-ink-900/40 p-4">
                <p className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-3 inline-flex items-center gap-2">
                  <MapPin size={12} weight="bold" /> Top cities
                </p>
                {data.top_cities.length === 0 ? <p className="text-xs text-zinc-500">No data yet</p> : (
                  <ul className="space-y-1.5">
                    {data.top_cities.slice(0, 15).map((c) => (
                      <li key={`${c.country}-${c.region}-${c.city}`} className="flex items-center justify-between text-sm">
                        <span className="text-zinc-300 truncate">{c.city}, {c.region} · {c.country}</span>
                        <span className="font-mono text-neon-cyan tabular-nums">{c.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-lg border border-ink-600 bg-ink-900/40 p-4">
                <p className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-3">Top postal / ZIP codes</p>
                {data.top_postal.length === 0 ? (
                  <p className="text-xs text-zinc-500">No ZIP data yet — set <code className="text-neon-cyan">GEO_PROVIDER=ipinfo</code> + <code className="text-neon-cyan">IPINFO_TOKEN</code> in <code>/app/backend/.env</code> for postal-level accuracy.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.top_postal.slice(0, 15).map((p) => (
                      <li key={p.postal} className="flex items-center justify-between text-sm font-mono">
                        <span className="text-zinc-300">{p.postal}</span>
                        <span className="text-neon-cyan tabular-nums">{p.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillRank, setDrillRank] = useState(null);
  const [drillCountry, setDrillCountry] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/analytics");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="analytics-loading">Loading analytics…</div>;
  if (!data) return <div className="text-zinc-500 text-sm">No data</div>;

  return (
    <section className="space-y-6" data-testid="analytics-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-1 inline-flex items-center gap-2">
            <Globe size={14} weight="fill" /> Analytics & Geographic Reach
          </p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">Where the Shoow is hitting.</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Visit + play tracking with geolocation. Currently using <span className="text-neon-cyan font-mono">{data.geo_provider}</span> ({data.geo_paid_enabled ? "paid postal accuracy" : "free city-level"}).{" "}
            {!data.geo_paid_enabled && <span className="text-zinc-400">Upgrade to <a href="https://ipinfo.io" target="_blank" rel="noopener noreferrer" className="text-neon-cyan hover:underline">ipinfo.io</a> for ZIP — set <code className="text-zinc-300">GEO_PROVIDER=ipinfo</code> + <code className="text-zinc-300">IPINFO_TOKEN</code> in backend/.env.</span>}
          </p>
        </div>
        <button onClick={load} className="px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan text-xs" data-testid="analytics-refresh">Reload</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total visits" value={data.total_visits} icon={Users} />
        <StatCard label="Total plays" value={data.total_plays} icon={Play} />
        <StatCard label="Countries reached" value={data.top_countries.length} icon={Globe} />
        <StatCard label="Geo-tagged hits" value={data.visit_pins.length} icon={MapPin} />
      </div>

      {data.visit_pins.length > 0 ? <WorldMap pins={data.visit_pins} /> : (
        <div className="rounded-lg border border-ink-600 bg-ink-800/30 p-6 text-center text-sm text-zinc-500">
          No geo-tagged visits yet — the map will fill in as real visitors come in.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-5">
          <p className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-3">Plays by song · click for drill-down</p>
          <ul className="space-y-1.5">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((rank) => {
              const row = data.plays_by_slot.find((p) => p.rank === rank);
              const count = row?.count || 0;
              return (
                <li key={rank}>
                  <button
                    onClick={() => setDrillRank(rank)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded hover:bg-ink-700/40 text-sm transition-colors"
                    data-testid={`analytics-slot-${rank}`}
                  >
                    <span className="font-mono text-xs text-neon-cyan">#{String(rank).padStart(2, "0")}</span>
                    <div className="flex-1 h-1.5 bg-ink-900 rounded">
                      <div className="h-full bg-neon-cyan rounded" style={{ width: `${Math.min(100, count * 5)}%` }} />
                    </div>
                    <span className="font-mono tabular-nums text-white w-12 text-right">{count}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="rounded-lg border border-ink-600 bg-ink-800/40 p-5">
          <p className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-3">Top countries <span className="text-zinc-600 normal-case font-normal">— click for city breakdown</span></p>
          {data.top_countries.length === 0 ? (
            <p className="text-xs text-zinc-500">No country data yet.</p>
          ) : (
            <ul className="space-y-1">
              {data.top_countries.slice(0, 12).map((c) => (
                <li key={c.country}>
                  <button
                    onClick={() => setDrillCountry(c.country)}
                    className="w-full flex items-center justify-between text-sm px-2 py-1.5 -mx-2 rounded hover:bg-ink-700/40 hover:text-neon-cyan transition-colors group"
                    data-testid={`country-row-${c.country}`}
                  >
                    <span className="text-zinc-300 font-mono group-hover:text-neon-cyan">{c.country}</span>
                    <span className="font-mono text-neon-cyan tabular-nums">{c.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {drillRank && <SlotDrillDown rank={drillRank} onClose={() => setDrillRank(null)} />}
      {drillCountry && <CountryDrillDown country={drillCountry} onClose={() => setDrillCountry(null)} />}
    </section>
  );
}

function CountryDrillDown({ country, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/analytics/country/${encodeURIComponent(country)}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ error: true }));
  }, [country]);
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm p-4 flex items-start justify-center pt-16 overflow-auto" onClick={onClose} data-testid="country-drilldown">
      <div className="w-full max-w-3xl rounded-xl border border-ink-600 bg-ink-900 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-ink-600 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-zinc-500">Country</p>
            <h3 className="text-xl font-black tracking-tight mt-1">{country}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/60 flex items-center justify-center" data-testid="country-drilldown-close" aria-label="Close">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {!data ? (
            <p className="text-zinc-500 text-sm">Loading…</p>
          ) : data.error ? (
            <p className="text-neon-magenta text-sm">Could not load detail</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Plays" value={data.total_plays || 0} icon={Globe} />
                <StatCard label="Clicks" value={data.total_clicks || 0} icon={Globe} />
                <StatCard label="Visits" value={data.total_visits || 0} icon={Globe} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">Cities</p>
                {data.cities?.length ? (
                  <ul className="space-y-1 max-h-72 overflow-y-auto">
                    {data.cities.map((c) => (
                      <li key={`${c.city}-${c.region}`} className="grid grid-cols-12 gap-2 items-center text-sm px-2 py-1.5 rounded hover:bg-ink-800/40">
                        <span className="col-span-5 text-zinc-200 truncate" data-testid={`country-city-${c.city}`}>{c.city}{c.region ? <span className="text-zinc-500"> · {c.region}</span> : null}</span>
                        <span className="col-span-2 text-right text-xs font-mono text-neon-cyan">{c.plays} plays</span>
                        <span className="col-span-2 text-right text-xs font-mono text-amber-300">{c.clicks} clicks</span>
                        <span className="col-span-2 text-right text-xs font-mono text-zinc-400">{c.visits} visits</span>
                        <span className="col-span-1 text-right text-xs font-mono text-zinc-500">{c.total}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-zinc-500">No city-level data captured yet.</p>
                )}
              </div>
              {data.top_slots?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-2">Most played slots here</p>
                  <ul className="flex flex-wrap gap-2">
                    {data.top_slots.map((s) => (
                      <li key={s.rank} className="text-xs px-2 py-1 rounded border border-ink-600 bg-ink-800/40 font-mono">
                        #{String(s.rank).padStart(2, "0")} <span className="text-neon-cyan ml-1">{s.plays} plays</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
